/**
 * Dữ liệu mẫu và máy chủ giả của phân hệ Quản lý module (plugin platform).
 *
 * Dùng khi `?mock=` trên URL (README mục "Xem từng trạng thái") hoặc chạy
 * `pnpm dev` trơn chưa đăng nhập Keycloak thật — xem `dungApiThat()` ở
 * `lib/api/http.ts`. Máy chủ giả áp **cùng luật trạng thái**
 * (`modules/quan-ly-module/nghiep-vu/thao-tac.ts`) và cùng `error_code` dự
 * kiến của ADR-0045/0046, để form không im lặng bỏ qua nhánh lỗi.
 *
 * Hình dạng đúng theo `ModuleVersion` thật (`openapi/platform-api.yaml`):
 * không có trường `permissions`, `sdk_version`, `ket_qua_kiem` — những thứ đó
 * không tồn tại trên response thật của `GET /api/v1/platform/modules`.
 *
 * Mỗi nhánh có ít nhất một bản ghi, kể cả nhánh xấu: module đang chờ duyệt,
 * module bị từ chối (kèm lý do), module có phiên bản nháp chưa nộp.
 */
import type { ApiError } from '../lib/errors';
import { duyetDuoc, tuChoiDuoc } from '../modules/quan-ly-module/nghiep-vu/thao-tac';
import type { ModulePackageResult, ModuleVersion, TrangThaiPhienBan } from '../modules/quan-ly-module/types';

/** `sub` của người đang đăng nhập (VENDOR mẫu) — khớp tài khoản `doitac@doitac.vn` ở `nhan-su-nen-tang.ts`. */
const NGUOI_NOP_MAU = 'ns-008';
const NGUOI_DUYET_MAU = '5b0e8c1d-3a7f-4e2b-9d6c-1f0a2b3c4d5e';

function truocDay(gio: number): string {
  return new Date(Date.now() - gio * 3_600_000).toISOString();
}

function phienBan(phan: Partial<ModuleVersion> & Pick<ModuleVersion, 'version_id' | 'module_id' | 'code' | 'name' | 'vendor' | 'version' | 'status'>): ModuleVersion {
  return {
    submitted_at: truocDay(48),
    submitted_by: NGUOI_NOP_MAU,
    reviewed_at: null,
    reviewed_by: null,
    review_note: null,
    ...phan,
  };
}

/** `module_version` phẳng, đúng hình dạng `GET /api/v1/platform/modules` trả về. */
export const MOCK_PHIEN_BAN: ModuleVersion[] = [
  // Module mẫu có giao diện trong gói (ADR-0096): màn chi tiết có khối xem trước, nạp từ route tệp giao diện.
  phienBan({
    version_id: 'ver-demo-3', module_id: 'mod-demo-sdk', code: 'demo-sdk', name: 'Demo SDK', vendor: 'FoxAI',
    version: '0.3.0', status: 'PUBLISHED',
    submitted_at: truocDay(2), reviewed_at: truocDay(1), reviewed_by: NGUOI_DUYET_MAU,
    frontend_entry: 'remoteEntry.js',
    menus: [
      { path: '/', label: 'Tổng quan', permission: 'demo-sdk:tong-quan:read' },
      { path: '/cong-viec', label: 'Công việc', permission: 'demo-sdk:cong-viec:read' },
    ],
  }),
  phienBan({
    version_id: 'ver-crm-1', module_id: 'mod-crm', code: 'crm', name: 'Quản lý khách hàng', vendor: 'FoxAI',
    version: '1.0.0', status: 'PUBLISHED',
    submitted_at: truocDay(24 * 30), reviewed_at: truocDay(24 * 29), reviewed_by: NGUOI_DUYET_MAU,
  }),
  phienBan({
    version_id: 'ver-crm-2', module_id: 'mod-crm', code: 'crm', name: 'Quản lý khách hàng', vendor: 'FoxAI',
    version: '1.1.0', status: 'IN_REVIEW',
  }),
  phienBan({
    version_id: 'ver-bc-1', module_id: 'mod-bao-cao', code: 'bao-cao', name: 'Báo cáo doanh nghiệp', vendor: 'FoxAI',
    version: '1.0.0', status: 'PUBLISHED',
    submitted_at: truocDay(24 * 60), reviewed_at: truocDay(24 * 59), reviewed_by: NGUOI_DUYET_MAU,
  }),
  phienBan({
    version_id: 'ver-kt-1', module_id: 'mod-ke-toan', code: 'ke-toan', name: 'Kế toán', vendor: 'Đối tác Kế toán Việt',
    version: '0.9.0', status: 'REJECTED',
    reviewed_at: truocDay(12), reviewed_by: NGUOI_DUYET_MAU,
    review_note: 'Quyền yêu cầu vượt phạm vi mô tả: "ke-toan:*" không đúng quy ước {resource}:{action}.',
  }),
  phienBan({
    version_id: 'ver-kv-1', module_id: 'mod-kho-van', code: 'kho-van', name: 'Kho vận', vendor: 'Đối tác Kho vận ABC',
    version: '0.1.0', status: 'DRAFT',
    submitted_at: truocDay(1),
  }),
  phienBan({
    version_id: 'ver-cu-1', module_id: 'mod-cu', code: 'module-cu', name: 'Module cũ', vendor: 'FoxAI',
    version: '1.0.0', status: 'DEPRECATED',
    submitted_at: truocDay(24 * 200), reviewed_at: truocDay(24 * 199), reviewed_by: NGUOI_DUYET_MAU,
  }),
];

export function danhSachPhienBanGia(): ModuleVersion[] {
  return MOCK_PHIEN_BAN;
}

function loi(code: string, status: number, message: string): Promise<never> {
  const e: ApiError = { code, status, message };
  return Promise.reject(e);
}

/**
 * Giả lập nộp gói (`POST /api/v1/vendor/modules`). Luôn tạo một dòng
 * `module_version` mới ở `IN_REVIEW` và thêm vào `MOCK_PHIEN_BAN` — đơn giản
 * hoá cho MVP, không đọc `module.yaml` thật từ nội dung zip. Tệp tên chứa
 * "loi" trả lỗi `MODULE_MANIFEST_INVALID`, để soát nhánh lỗi mà không phải
 * chỉnh mock mỗi lần.
 */
export function nopGoiGia(tep: File): Promise<ModulePackageResult> {
  const laLoi = tep.name.toLowerCase().includes('loi');
  if (laLoi) {
    const e: ApiError = {
      code: 'MODULE_MANIFEST_INVALID',
      status: 422,
      message: '',
      details: [
        { pointer: '#/permissions', detail: 'Quyền "crm:khach-hang:xoa" dùng ở routes nhưng không khai ở permissions' },
        { pointer: '#/sdkVersion', detail: 'Thiếu trường bắt buộc sdkVersion' },
      ],
    };
    return Promise.reject(e);
  }
  const code = `demo-${Date.now()}`;
  const version = '0.1.0';
  MOCK_PHIEN_BAN.unshift(
    phienBan({
      version_id: `ver-${Date.now()}`,
      module_id: `mod-${Date.now()}`,
      code,
      name: `Nộp từ tệp ${tep.name}`,
      vendor: 'FoxAI',
      version,
      status: 'IN_REVIEW',
      submitted_at: new Date().toISOString(),
    }),
  );
  return Promise.resolve({ code, version, status: 'IN_REVIEW' });
}

export function duyetGia(code: string, version: string): Promise<ModuleVersion> {
  const hienTai = MOCK_PHIEN_BAN.find((p) => p.code === code && p.version === version);
  if (!hienTai) return loi('NOT_FOUND', 404, 'Không tìm thấy phiên bản này.');
  if (!duyetDuoc(hienTai.status)) {
    return loi('MODULE_STATUS_CONFLICT', 409, 'Phiên bản không ở trạng thái chờ duyệt.');
  }
  hienTai.status = 'PUBLISHED' as TrangThaiPhienBan;
  hienTai.reviewed_at = new Date().toISOString();
  hienTai.reviewed_by = NGUOI_DUYET_MAU;
  return Promise.resolve({ ...hienTai });
}

export function tuChoiGia(code: string, version: string, reason: string): Promise<ModuleVersion> {
  const hienTai = MOCK_PHIEN_BAN.find((p) => p.code === code && p.version === version);
  if (!hienTai) return loi('NOT_FOUND', 404, 'Không tìm thấy phiên bản này.');
  if (!tuChoiDuoc(hienTai.status)) {
    return loi('MODULE_STATUS_CONFLICT', 409, 'Phiên bản không ở trạng thái chờ duyệt.');
  }
  hienTai.status = 'REJECTED' as TrangThaiPhienBan;
  hienTai.reviewed_at = new Date().toISOString();
  hienTai.reviewed_by = NGUOI_DUYET_MAU;
  hienTai.review_note = reason;
  return Promise.resolve({ ...hienTai });
}

/** Giả lập `DELETE /api/v1/platform/modules/{code}`: xoá mọi phiên bản của module. */
export function xoaModuleGia(code: string): Promise<void> {
  if (!MOCK_PHIEN_BAN.some((p) => p.code === code)) return loi('NOT_FOUND', 404, 'Không tìm thấy module này.');
  for (let i = MOCK_PHIEN_BAN.length - 1; i >= 0; i--) {
    if (MOCK_PHIEN_BAN[i].code === code) MOCK_PHIEN_BAN.splice(i, 1);
  }
  return Promise.resolve();
}
